import { createContext, use, useCallback, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthenticatedUser, ChangePasswordRequest, LoginResponse } from '@fametrue/shared';
import { ApiError, apiRequest } from '../lib/api';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  acceptInvite: (token: string, password: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_KEY = ['session'] as const;

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();

  /**
   * La sessione vive nei cookie httpOnly, che JavaScript non può leggere:
   * l'unico modo per sapere chi è l'utente è chiederlo all'API.
   */
  const session = useQuery({
    queryKey: SESSION_KEY,
    queryFn: async (): Promise<AuthenticatedUser | null> => {
      try {
        const res = await apiRequest<LoginResponse>('/api/auth/me');
        return res.user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      apiRequest<LoginResponse>('/api/auth/login', { method: 'POST', body: input }),
    onSuccess: (res) => queryClient.setQueryData(SESSION_KEY, res.user),
  });

  const acceptInviteMutation = useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      apiRequest<LoginResponse>('/api/auth/accept-invite', { method: 'POST', body: input }),
    onSuccess: (res) => queryClient.setQueryData(SESSION_KEY, res.user),
  });

  /**
   * Il cambio password revoca tutte le sessioni dell'utente e ne apre una
   * nuova: il server riemette i cookie, quindi la finestra da cui si fa il
   * cambio resta collegata, mentre gli altri dispositivi devono rientrare.
   * La sessione si rilegge comunque dall'API, per non fidarsi di quel passaggio.
   */
  const changePasswordMutation = useMutation({
    mutationFn: (input: ChangePasswordRequest) =>
      apiRequest<void>('/api/auth/change-password', { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
  });

  const login = useCallback(
    async (email: string, password: string) => {
      await loginMutation.mutateAsync({ email, password });
    },
    [loginMutation],
  );

  const acceptInvite = useCallback(
    async (token: string, password: string) => {
      await acceptInviteMutation.mutateAsync({ token, password });
    },
    [acceptInviteMutation],
  );

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      await changePasswordMutation.mutateAsync({ currentPassword, newPassword });
    },
    [changePasswordMutation],
  );

  const logout = useCallback(async () => {
    try {
      await apiRequest<void>('/api/auth/logout', { method: 'POST' });
    } catch {
      // Una disconnessione che fallisce lato rete non deve bloccare l'utente qui:
      // lo stato locale viene comunque azzerato, e i cookie di sessione sono già
      // scaduti o revocati nei casi in cui questa chiamata restituisce errore.
    } finally {
      // L'ordine conta. `queryClient.clear()` distrugge la voce di cache a cui è
      // agganciato l'observer di useQuery: la voce ricreata subito dopo da
      // setQueryData sarebbe un oggetto diverso, l'observer resterebbe legato a
      // quella distrutta e l'interfaccia continuerebbe a mostrare l'utente
      // precedente. Si azzera prima la sessione, poi si scartano le altre cache.
      queryClient.setQueryData(SESSION_KEY, null);
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== SESSION_KEY[0],
      });
    }
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session.data ?? null,
      isLoading: session.isPending,
      login,
      acceptInvite,
      changePassword,
      logout,
    }),
    [session.data, session.isPending, login, acceptInvite, changePassword, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth richiede AuthProvider');
  return context;
}
